import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import type { TCommunicationsMessage } from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { PhoneNumberText } from "../../../../common/native";
import { MessageBubble } from "../../messages/shared";
import { styles } from "../HomeConversations.styles";
import type { TMessageConversationListItem } from "../types";
import MessageComposer from "./MessageComposer";
import MessageStatusBadge from "./MessageStatusBadge";

type MessageThreadCardProps = {
  isMessagesTab: boolean;
  loading: boolean;
  error: string | null;
  selectedConversation: TMessageConversationListItem | null;
  isComposingNewMessage: boolean;
  messages: TCommunicationsMessage[];
  isMobile: boolean;
  panelMode?: boolean;
  assignSubmitting: boolean;
  onOpenAssignPatientModal: () => void;
  onMobileBack: () => void;
  onRetry: () => void;
  formatDateTime: (value?: string | null) => string;
  composer: {
    fromNumbers: string[];
    fromE164: string;
    onFromE164Change: (fromE164: string) => void;
    toCountryCode: string;
    toLocalNumber: string;
    onToCountryCodeChange: (value: string) => void;
    onToLocalNumberChange: (value: string) => void;
    draftBody: string;
    onDraftBodyChange: (value: string) => void;
    onSend: () => void;
    submitting: boolean;
    disabled: boolean;
    error: string | null;
  };
};

const normalizePhone = (value?: string | null): string =>
  (value || "").trim().toLowerCase();

const isOutboundMessage = (
  message: TCommunicationsMessage,
  participantE164?: string | null,
): boolean => {
  const direction = (message.direction || "").trim().toLowerCase();
  if (direction === "outbound" || direction.startsWith("outbound")) {
    return true;
  }
  if (direction === "inbound" || direction.startsWith("inbound")) {
    return false;
  }

  const normalizedParticipant = normalizePhone(participantE164);
  const normalizedFrom = normalizePhone(message.from_e164);
  const normalizedTo = normalizePhone(message.to_e164);

  if (!normalizedParticipant) {
    return false;
  }

  if (normalizedTo === normalizedParticipant && normalizedFrom) {
    return true;
  }
  if (normalizedFrom === normalizedParticipant) {
    return false;
  }

  return false;
};

const MessageThreadCard: React.FC<MessageThreadCardProps> = ({
  isMessagesTab,
  loading,
  error,
  selectedConversation,
  isComposingNewMessage,
  messages,
  isMobile,
  panelMode = false,
  assignSubmitting,
  onOpenAssignPatientModal,
  onMobileBack,
  onRetry,
  formatDateTime,
  composer,
}) => {
  const showMobileBackNav =
    isMobile && (selectedConversation || isComposingNewMessage);

  return (
    <View
      style={[
        panelMode ? styles.messagesThreadPanel : styles.card,
        panelMode ? styles.messagesThreadPanelStretch : null,
        !isMessagesTab && styles.hiddenSection,
      ]}
    >
      {showMobileBackNav ? (
        <Pressable
          style={styles.mobileMessagesBackNav}
          onPress={onMobileBack}
          accessibilityLabel="Back to messages inbox"
        >
          <Ionicons
            name="chevron-back"
            size={16}
            color={ADMIN_TOKENS.color.accentDark}
          />
          <Text style={styles.mobileMessagesBackNavText}>Messages</Text>
        </Pressable>
      ) : null}

      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>
          {isComposingNewMessage ? "New Message" : "Conversation"}
        </Text>
        <View style={styles.messageInboxHeaderActions}>
          {selectedConversation && !isComposingNewMessage ? (
            <Pressable
              style={[
                styles.callAgainButton,
                styles.messageAssignButton,
                assignSubmitting && styles.callAgainButtonDisabled,
              ]}
              onPress={onOpenAssignPatientModal}
              disabled={assignSubmitting}
              accessibilityLabel="Assign this conversation to a patient"
            >
              <Ionicons
                name="person-add-outline"
                size={16}
                color={ADMIN_TOKENS.color.accentDark}
              />
              <Text style={styles.callAgainButtonText}>Assign</Text>
            </Pressable>
          ) : null}
        </View>
      </View>

      {!selectedConversation && !isComposingNewMessage ? (
        <View style={styles.sectionStack}>
          <Text style={styles.emptyText}>
            Select a conversation from the inbox to view message history.
          </Text>
          <Text style={styles.emptyText}>
            Or tap New Message to send to a new phone number.
          </Text>
        </View>
      ) : loading && !isComposingNewMessage ? (
        <View style={styles.loadingInlineRow}>
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading messages...</Text>
        </View>
      ) : error ? (
        <View style={styles.sectionStack}>
          <Text style={styles.inlineErrorText}>{error}</Text>
          <Pressable
            style={styles.callAgainButton}
            onPress={onRetry}
            accessibilityLabel="Retry loading messages"
          >
            <Text style={styles.callAgainButtonText}>Retry</Text>
          </Pressable>
        </View>
      ) : (
        <View
          style={[
            styles.sectionStack,
            panelMode ? styles.messageThreadSectionMobile : null,
          ]}
        >
          {selectedConversation ? (
            <View style={styles.sectionStack}>
              <Text style={styles.callNumberText}>
                {selectedConversation.patientLabel
                  ? `${selectedConversation.patientLabel} • `
                  : null}
                <PhoneNumberText
                  value={selectedConversation.participantE164}
                  style={styles.callNumberText}
                />
              </Text>
              <View style={styles.messageThreadBody}>
                {!messages.length ? (
                  <Text style={styles.emptyText}>
                    No messages yet. Send a message to start the thread.
                  </Text>
                ) : (
                  <View style={styles.messageBubbleList}>
                    {messages.map((message) => {
                      const isOutbound = isOutboundMessage(
                        message,
                        selectedConversation.participantE164,
                      );

                      return (
                        <MessageBubble
                          key={message.message_uuid}
                          body={message.body}
                          isOutbound={isOutbound}
                          timestamp={formatDateTime(
                            message.provider_created_at || message.created_at,
                          )}
                          statusNode={
                            isOutbound ? (
                              <MessageStatusBadge status={message.status} />
                            ) : null
                          }
                        />
                      );
                    })}
                  </View>
                )}
              </View>
            </View>
          ) : null}

          <MessageComposer
            fromNumbers={composer.fromNumbers}
            fromE164={composer.fromE164}
            onFromE164Change={composer.onFromE164Change}
            toCountryCode={composer.toCountryCode}
            toLocalNumber={composer.toLocalNumber}
            onToCountryCodeChange={composer.onToCountryCodeChange}
            onToLocalNumberChange={composer.onToLocalNumberChange}
            showToField={isComposingNewMessage}
            draftBody={composer.draftBody}
            onDraftBodyChange={composer.onDraftBodyChange}
            onSend={composer.onSend}
            submitting={composer.submitting}
            disabled={composer.disabled}
            error={composer.error}
          />
        </View>
      )}
    </View>
  );
};

export default MessageThreadCard;
